import DevToolsFloatingButton from '@components/DevToolsFloatingButton'
import UpdateBanner from '@components/UpdateBanner'
import { datadogRum } from '@datadog/browser-rum'
import { SuspendedModal } from '@modules/tenant/SuspendedModal'
import { LicenseInfo } from '@mui/x-license'
import { SBAsyncSelectProvider } from '@supplement-bacon/react-async-select'
import { LoadingFullScreen } from '@supplement-bacon/react-toolkit/layouts'
import {
  APIResponse,
  MessageResponse,
} from '@supplement-bacon/react-toolkit/models'
import { setupAxios } from '@supplement-bacon/react-toolkit/utils'
import '@supplement-bacon/sbrm-react/index.css'
import { App as ANTDApp, ConfigProvider } from 'antd'
import axios, { AxiosResponse } from 'axios'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import { Outlet, useNavigate } from 'react-router-dom'
import AuthDependantProviders from './components/AuthDependantProviders'
import { DocumentTitle } from './components/DocumentTitle'
import ForbiddenRequestInterceptor from './components/ForbiddenRequestInterceptor'
import { MobileDisclaimer } from './components/MobileDisclaimer'
import SessionIdentity from './components/SessionIdentity'
import { ThemeProvider, useTheme } from './contexts/ThemeContext'
import { Admin } from './models/Admin'
import { AuthProvider } from './modules/auth/contexts/Auth'
import Tenant from './modules/tenant/Tenant'
import { GleapRedactUser } from './services/GleapService'
import { useAppDispatch } from './store/hooks'
import { generateConfig } from './utils/Entities'
import {
  FORGOT_PASSWORD_URL,
  GET_LOGGED_USER_INFOS,
  LOGIN_URL,
  LOGOUT_URL,
  REGISTER_URL,
  RESET_PASSWORD_URL,
} from './utils/urls'

export const DEFAULT_SUCCESS = 'Request completed successfully!'
export const DEFAULT_ERROR = 'An error occurred. Please try again.'

function InnerApp() {
  const { themeConfig } = useTheme()

  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const config = dispatch(generateConfig())

  // DayJS plugins
  dayjs.extend(isSameOrBefore)
  dayjs.extend(isSameOrAfter)
  dayjs.extend(relativeTime)
  dayjs.extend(isBetween)
  dayjs.extend(utc)

  // MUI License key
  LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENCE_KEY)

  // Configure Axios request & response interceptors
  setupAxios(axios, 'en', (error) => {
    // Prevent redirect loop: skip if already on the logout page or if the
    // failing request is the logout call itself (session already expired)
    if (
      window.location.pathname === '/auth/going-to-logout' ||
      error.config?.url === LOGOUT_URL
    ) {
      return
    }
    if (
      (error.response as AxiosResponse<APIResponse<MessageResponse>>)?.data
        ?.data?.message === 'auth.2fa.required'
    ) {
      const queryParameters = new URLSearchParams(window.location.search)
      const returnTo =
        queryParameters.get('returnTo') ?? window.location.pathname
      sessionStorage.setItem('2fa.returnTo', returnTo)
      window.location.href = '/auth/2FA'
      return
    }
    console.error('HTTP error 401 - Going to logout')
    window.location.href = '/auth/going-to-logout'
  })

  // setupAxios only handles 401. A 403 (authenticated but lacking the
  // permission the endpoint requires) is handled by
  // <ForbiddenRequestInterceptor /> below, which needs the antd App context.

  return (
    <ConfigProvider theme={themeConfig}>
      <Tenant.Favicon />
      <ANTDApp>
        <ForbiddenRequestInterceptor />
        <AuthProvider<Admin>
          loadingLayout={<LoadingFullScreen />}
          urls={{
            loggedUserInfos: GET_LOGGED_USER_INFOS,
            login: LOGIN_URL,
            logout: LOGOUT_URL,
            register: REGISTER_URL,
            forgotPassword: FORGOT_PASSWORD_URL,
            resetPassword: RESET_PASSWORD_URL,
          }}
          loggedIn={async () => {
            // Gleap and Datadog identification runs from <SessionIdentity />
            // (fires on every session, including reloads), so it is
            // intentionally not called here.

            // Redirect to logged part
            const queryParameters = new URLSearchParams(window.location.search)
            const returnTo = queryParameters.get('returnTo')
            navigate(returnTo ? returnTo : '/')
          }}
          loggedOut={async () => {
            // dispatch(resetStore())

            // Finally redirect to login
            GleapRedactUser()
            datadogRum.clearUser()
            navigate('/auth/login')
          }}
        >
          <SessionIdentity />
          <MobileDisclaimer>
            <SuspendedModal>
              <SBAsyncSelectProvider
                config={{
                  entities: config,
                }}
              >
                <AuthDependantProviders config={config}>
                  <DocumentTitle>
                    <Outlet />
                  </DocumentTitle>
                </AuthDependantProviders>
              </SBAsyncSelectProvider>
            </SuspendedModal>
          </MobileDisclaimer>
          <UpdateBanner />
          {/* Inside the auth context: in production it is shown to internal admins only */}
          <DevToolsFloatingButton />
        </AuthProvider>
      </ANTDApp>
    </ConfigProvider>
  )
}
const App = () => (
  <ThemeProvider>
    <InnerApp />
  </ThemeProvider>
)
export default App
